/**
 * Shared utilities for the Attack Simulation Training pages.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchAllCustomers } from '@/capabilities/customer-lifecycle/public/hooks/useCustomers';
import { extractList } from '@/platform/api-client/helpers';
import { z } from 'zod';
import { PageTabs } from '@/platform/design-system/components/shared/PageTabs';
import { useWorkflowGroup } from '@/workflows/automation/hooks/useWorkflowGroup';
import { PageTransition } from '@/platform/design-system/components/ui/PageTransition';

export const surfaceClass =
  'rounded-2xl border border-[rgb(var(--color-border-primary)/0.68)] bg-[linear-gradient(180deg,rgba(var(--color-bg-elevated),0.96),rgba(var(--color-bg-primary),0.94))] shadow-[0_22px_55px_rgba(15,23,42,0.08)]';
export const subtleTextClass = 'text-[rgb(var(--color-text-tertiary))]';
export const bodyTextClass = 'text-[rgb(var(--color-text-secondary))]';
export const headingClass = 'text-[rgb(var(--color-text-primary))]';

// Coverage + Repeat Offenders are now in-tab views of the Compliance > Attack Sim
// surface; the kept Create/Detail pages link back to it.
export const attackSimTabs = [
  { label: 'Attack Sim', href: '/portfolio/compliance/attacksim' },
];

export interface M365Customer {
  id: string;
  name: string;
  m365TenantId: string;
}

const m365CustomerSchema: z.ZodType<M365Customer> = z.object({
  id: z.string(), name: z.string(), m365TenantId: z.string(),
});

export function useM365Customers() {
  return useQuery<M365Customer[]>({
    queryKey: ['attacksim-m365-customers'],
    // Every active customer (cursor-paged), not just the first 200.
    queryFn: async ({ signal }) => {
      const data = { data: await fetchAllCustomers({ status: 'active' }, signal) };
      return extractList(data, m365CustomerSchema, 'M365 customers').filter((c) => c.m365TenantId);
    },
  });
}

interface AttackSimHeaderProps {
  tenantId: string;
  onTenantChange: (tenantId: string) => void;
  title: string;
  subtitle?: string;
  rightSlot?: React.ReactNode;
}

export function AttackSimHeader({ tenantId, onTenantChange, title, subtitle, rightSlot }: AttackSimHeaderProps) {
  const { palette } = useWorkflowGroup();
  const { data: customers = [], isLoading } = useM365Customers();
  return (
    <div className="space-y-4">
      <PageTabs tabs={attackSimTabs} accentColor={palette.accent} />
      <div className={`${surfaceClass} p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4`}>
        <div>
          <h1 className={`text-xl font-semibold ${headingClass}`}>{title}</h1>
          {subtitle && <p className={`mt-1 text-sm ${subtleTextClass}`}>{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <label className={`text-xs uppercase tracking-wide ${subtleTextClass}`}>Target tenant</label>
          <select
            className="rounded-lg border border-[rgb(var(--color-border-primary)/0.68)] bg-[rgb(var(--color-bg-primary))] px-3 py-2 text-sm text-[rgb(var(--color-text-primary))]"
            value={tenantId}
            onChange={(e) => onTenantChange(e.target.value)}
            disabled={isLoading}
          >
            <option value="">{isLoading ? 'Loading…' : 'Select a customer'}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.m365TenantId}>
                {c.name}
              </option>
            ))}
          </select>
          {rightSlot}
        </div>
      </div>
    </div>
  );
}

export function EmptyState({ title, message }: { title: string; message: string }) {
  return (
    <div className={`${surfaceClass} p-10 text-center`}>
      <h3 className={`text-base font-semibold ${headingClass}`}>{title}</h3>
      <p className={`mt-2 text-sm ${bodyTextClass}`}>{message}</p>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <PageTransition className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6 text-sm text-rose-200">
      <strong className="block text-rose-100">Failed to load from Microsoft Graph</strong>
      <span>{message}</span>
    </PageTransition>
  );
}
